import type { OrganizationState } from '../../types';
import type { FunctionViewModel, TaskViewModel } from '@trinnvis/dabih-api-client';
import { employeeNamesAsText } from '../employees/employee-names-as-text';
import { compareSortAndName } from './compare-sort-and-name';
import { createSelector } from '@reduxjs/toolkit';
import { getOrganization } from '../organization/getOrganization';
import { functionsNotDeleted } from './functions-not-deleted';
import { tasksNotDeleted } from './tasks-not-deleted';
import { currentPageId } from '../path';

import type { FunctionWithTasksForPageViewModel } from './function-with-tasks-for-page-view-model';
import { getFeatureStates } from 'src/store/selectors/features/getFeatureStates';
import type { FeatureStates } from 'src/store/selectors/featureStates';

export function undefinedToEmpty(s?: string): string {
  if (s) return s;
  else return '';
}

export function _functionsForPage(
  organization: OrganizationState,
  functions: FunctionViewModel[],
  tasks: TaskViewModel[],
  pageId: number,
  features: FeatureStates,
): FunctionWithTasksForPageViewModel[] {
  if (organization === undefined) {
    return [];
  }
  const functionsForPage: FunctionWithTasksForPageViewModel[] = [];
  functions.forEach(function (functionItem) {
    const result: FunctionWithTasksForPageViewModel = Object.assign({}, functionItem, {
      tasks: [],
      sort: '',
      assignedNames: '',
      href: '/account/' + organization.organizationId + '/' + pageId + '/functions/' + functionItem.uuid,
      onPage: false,
    });

    let functionHasTask = false;
    let functionHasRelatedTask = false;
    let functionIsResponsible = false;
    if (functionItem.type !== 'COMMON' && functionItem.pages.includes(pageId)) {
      result.onPage = true;
    }
    if (result.type === 'COMMON') {
      result.name = 'Fellesansvar';
      result.assignedNames = 'Alle';
    } else {
      result.assignedNames = employeeNamesAsText(functionItem, organization);
    }
    tasks.forEach(function (taskItem) {
      let taskIsRelated = false;
      // if features include quality and task has current function as responsible
      if (
        features.quality &&
        taskItem.responsibleFunctionUuid &&
        taskItem.responsibleFunctionUuid === functionItem.uuid
      ) {
        functionIsResponsible = true;
      }
      // if task is assigned to current function
      if (taskItem.functionUuid === functionItem.uuid) {
        functionHasTask = true;
        let taskHasResponsibleFunction = false;
        if (taskItem.responsibleFunctionUuid) {
          const functionViewModel = organization.functionsById[taskItem.responsibleFunctionUuid];
          if (functionViewModel === undefined) {
            console.error('Undefined responsible function ' + taskItem.responsibleFunctionUuid);
          } else {
            taskHasResponsibleFunction = true;
            // If responsible function for current task belongs to page
            if (functionViewModel.pages.includes(pageId)) {
              taskIsRelated = true;
              functionHasRelatedTask = true;
            }
          }
        }
        // Tasks assigned to the common function that have no responsible function should display on the page Ledelse
        if (functionItem.type === 'COMMON' && !taskHasResponsibleFunction && pageId === 357) {
          taskIsRelated = true;
          functionHasRelatedTask = true;
        }
        // If task is set to show on page
        const tasksByIdElement = organization.tasksById[taskItem.uuid];
        if (tasksByIdElement?.pages?.includes(pageId)) {
          taskIsRelated = true;
          functionHasRelatedTask = true;
        }
        if (result.onPage || taskIsRelated) {
          result.tasks.push(taskItem);
        }
      }
    });
    if (!functionHasTask) {
      result.sort = 'G';
      if (result.type === 'ROTATED') {
        result.sort = 'F';
      }
      if (result.type === 'SINGLE') {
        result.sort = 'E';
      }
    }
    if (result.onPage) {
      result.sort = 'D';
      if (result.type === 'ROTATED') {
        result.sort = 'C';
      }
      if (result.type === 'SINGLE') {
        if (functionIsResponsible) {
          result.sort = 'A';
        } else {
          result.sort = 'B';
        }
      }
    } else {
      result.sort = 'K';
      if (result.type === 'ROTATED') {
        result.sort = 'J';
      }
      if (result.type === 'SINGLE') {
        if (functionIsResponsible) {
          result.sort = 'H';
        } else {
          result.sort = 'I';
        }
      }
      if (result.type === 'COMMON') {
        result.sort = 'L';
      }
    }
    if (result.onPage || functionHasRelatedTask) {
      result.tasks.sort(function (a, b) {
        return undefinedToEmpty(a.name).toLowerCase().localeCompare(undefinedToEmpty(b.name).toLowerCase());
      });
      functionsForPage.push(result);
    }
  });
  return functionsForPage.sort(compareSortAndName);
}

export const functionsWithTasksForCurrentPage = createSelector(
  getOrganization,
  functionsNotDeleted,
  tasksNotDeleted,
  currentPageId,
  getFeatureStates,
  (organization, functions, tasks, pageId, features) => {
    if (organization === undefined || pageId === undefined) {
      return [];
    }
    const regularTasks = tasks.filter(function (item) {
      return !item.locked;
    });
    return _functionsForPage(organization, functions, regularTasks, pageId, features);
  },
);
